import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminApi } from '../../lib/adminApi';

const contentTypes = [
  ['news', 'News'], ['events', 'Events'], ['notices', 'Notices'], ['departments', 'Departments'],
  ['downloads', 'Downloads'], ['staff', 'Staff'], ['journal_articles', 'Journal Articles'],
  ['journal_issues', 'Journal Issues'], ['alumni', 'Alumni'], ['partnerships', 'Partnerships'], ['projects', 'Projects'],
];

const badge = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-blue-100 text-blue-700',
  rejected: 'bg-red-100 text-red-700',
  published: 'bg-emerald-100 text-emerald-700',
};

export default function AdminContentApprovals() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ content_type: 'news', content_id: '', request_note: '' });

  async function loadItems() {
    try {
      setLoading(true);
      setError('');
      const data = await adminApi(`/api/admin/content-approvals?status=${status}`);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadItems(); }, [status]);

  async function createRequest(e) {
    e.preventDefault();
    try {
      await adminApi('/api/admin/content-approvals', { method: 'POST', body: JSON.stringify(form) });
      setForm({ content_type: 'news', content_id: '', request_note: '' });
      await loadItems();
    } catch (err) {
      alert(err.message);
    }
  }

  async function review(id, action) {
    const review_note = window.prompt(`Optional note for ${action}:`) || '';
    try {
      await adminApi(`/api/admin/content-approvals/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ action, review_note }),
      });
      await loadItems();
    } catch (err) {
      alert(err.message);
    }
  }

  return (
    <AdminLayout title="Content Approval Workflow" subtitle="Request review, approve content and publish selected CMS records through a clear audit trail.">
      <form onSubmit={createRequest} className="mb-6 grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[220px_160px_1fr_auto]">
        <select value={form.content_type} onChange={(e) => setForm((p) => ({ ...p, content_type: e.target.value }))} className="rounded-xl border border-slate-200 p-3">
          {contentTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <input value={form.content_id} onChange={(e) => setForm((p) => ({ ...p, content_id: e.target.value }))} placeholder="Content ID" className="rounded-xl border border-slate-200 p-3" required />
        <input value={form.request_note} onChange={(e) => setForm((p) => ({ ...p, request_note: e.target.value }))} placeholder="Review note / reason" className="rounded-xl border border-slate-200 p-3" />
        <button className="rounded-xl bg-emerald-700 px-5 py-3 font-black text-white">Request Approval</button>
      </form>

      <div className="mb-4 flex flex-wrap gap-2">
        {['pending', 'approved', 'rejected', 'published', 'all'].map((value) => (
          <button key={value} onClick={() => setStatus(value)} className={`rounded-xl px-4 py-2 text-sm font-black ${status === value ? 'bg-emerald-700 text-white' : 'bg-white text-slate-700 ring-1 ring-slate-200'}`}>{value}</button>
        ))}
      </div>

      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">{error}</div>}
      {loading ? <div className="rounded-2xl bg-white p-6 font-bold text-slate-600 shadow-sm">Loading approval requests...</div> : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-wider text-slate-500">
              <tr><th className="px-5 py-4">Content</th><th className="px-5 py-4">Requested By</th><th className="px-5 py-4">Status</th><th className="px-5 py-4">Note</th><th className="px-5 py-4 text-right">Action</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((item) => (
                <tr key={item.id} className="align-top">
                  <td className="px-5 py-4"><div className="font-black text-slate-950">{item.content_title}</div><div className="text-xs text-slate-500">{item.content_type} #{item.content_id}</div></td>
                  <td className="px-5 py-4 text-slate-600">{item.requested_by_name || 'Admin'}</td>
                  <td className="px-5 py-4"><span className={`rounded-full px-3 py-1 text-xs font-black ${badge[item.status] || badge.pending}`}>{item.status}</span></td>
                  <td className="px-5 py-4 text-slate-600">{item.request_note || item.review_note || '-'}</td>
                  <td className="px-5 py-4">
                    <div className="flex flex-wrap justify-end gap-2">
                      <button onClick={() => review(item.id, 'approved')} className="rounded-lg border border-blue-200 px-3 py-2 font-bold text-blue-700 hover:bg-blue-50">Approve</button>
                      <button onClick={() => review(item.id, 'published')} className="rounded-lg border border-emerald-200 px-3 py-2 font-bold text-emerald-700 hover:bg-emerald-50">Publish</button>
                      <button onClick={() => review(item.id, 'rejected')} className="rounded-lg border border-red-200 px-3 py-2 font-bold text-red-700 hover:bg-red-50">Reject</button>
                    </div>
                  </td>
                </tr>
              ))}
              {!items.length && <tr><td colSpan="5" className="px-5 py-10 text-center font-bold text-slate-500">No approval request found.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
